import { ScrollArea } from '@zond/ui';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getAppName, getLogo } from '../../../utils/get-logo';

export default function TermsOfService() {
  const navigate = useNavigate();
  const { t } = useTranslation('legal');

  return (
    <div className="container mx-auto py-8 px-4 md:py-12 h-screen overflow-hidden">
      <div className="max-w-3xl mx-auto h-full flex flex-col">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <img src={getLogo()} alt={t('logoAlt')} className="h-6 w-6" />
            <span className="font-medium">{getAppName()}</span>
          </div>
          <button
            onClick={() => navigate('/')}
            className="rounded-full p-2 hover:bg-muted transition-colors"
            aria-label={t('common:actions.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <h1 className="text-3xl font-bold mb-8">{t('terms.title')}</h1>

        <div className="overflow-y-auto custom-scrollbar">
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('terms.sections.introduction')}</h2>
              <p className="mb-4">
                Welcome to Klarops. By accessing our website and using our services, you agree to these Terms
                of Service. Please read them carefully before using our platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('terms.sections.definitions')}</h2>
              <p className="mb-4">
                "Service" refers to the Klarops platform, including all features, functionalities, and user
                interfaces. "User" refers to any individual or entity that accesses or uses our Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('terms.sections.accountRegistration')}</h2>
              <p className="mb-4">
                To use our Service, you must create an account. You agree to provide accurate and complete
                information during registration and to keep this information updated.
              </p>
              <p className="mb-4">
                You are responsible for maintaining the confidentiality of your account credentials and for
                all activities that occur under your account.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('terms.sections.userObligations')}</h2>
              <p className="mb-4">When using our Service, you agree to:</p>
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Comply with all applicable laws and regulations</li>
                <li className="mb-2">Respect the intellectual property rights of others</li>
                <li className="mb-2">Not engage in any unauthorized access or use of the Service</li>
                <li className="mb-2">Not interfere with or disrupt the Service or servers</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('terms.sections.intellectualProperty')}</h2>
              <p className="mb-4">
                All content, features, and functionality of our Service are owned by Klarops and are protected
                by international copyright, trademark, and other intellectual property laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('terms.sections.privacy')}</h2>
              <p className="mb-4">
                Your privacy is important to us. Our collection and use of personal information is governed by
                our Privacy Policy, which is incorporated into these Terms of Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('terms.sections.termination')}</h2>
              <p className="mb-4">
                We reserve the right to terminate or suspend your account and access to the Service at our
                sole discretion, without notice, for conduct that we believe violates these Terms of Service
                or is harmful to other users, us, or third parties, or for any other reason.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('terms.sections.changes')}</h2>
              <p className="mb-4">
                We may modify these Terms of Service at any time. We will notify you of any material changes
                by posting the new Terms of Service on this page and updating the "Last Updated" date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('terms.sections.contact')}</h2>
              <p className="mb-4">
                If you have any questions about these Terms of Service, please contact us at
                support@klarops.com
              </p>
            </section>

            <div className="text-sm text-muted-foreground mt-8">
              {t('lastUpdated', { date: 'March 2024' })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
